import { Error } from "@progress/kendo-react-labels";
import { Window } from '@progress/kendo-react-dialogs';
import { Popup } from "@progress/kendo-react-popup";
import { filterBy } from "@progress/kendo-data-query";
import { MultiColumnComboBox, ComboBoxChangeEvent, ComboBoxFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { useContext, useEffect, useState, cloneElement, useRef } from "react";
import { protectedResources } from "../authConfig";
import { useFetchWithMsal } from "../hooks/useFetchWithMsal";
import { IStreckContext } from "./PvsContextProvider";

interface LabComboBoxProps {
    context: React.Context<IStreckContext>,
    title: string,
    verb: string,
    endpoint: string,
    selectedId?: number,
    required: boolean,
    handleChange: ((selectedId: number) => void),
    addObjectElement?: React.ReactNode,
    disabled?: boolean,
    children?: React.ReactNode
}

export const LabComboBox = (props: LabComboBoxProps) => {
    const [selected, setSelected] = useState({} as any);
    const [filteredData, setFilteredData] = useState(undefined as any[] | undefined);
    const context = useContext(props.context);
    const anchor = useRef<HTMLSpanElement | null>(null);

    const [showWindow, setShowWindow] = useState(false);
    const [showAddress, setShowAddress] = useState(false);

    const { execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const selectFromFiltered = () => {
        if (props.selectedId && filteredData) {
            const found: any = filteredData.find((item: any) => item.id === props.selectedId);
            if (found) {
                setSelected(found);
            }
        }
    }

    const filterChange = (filter: ComboBoxFilterChangeEvent) => {
        const filterData = context.data || [];
        const sconst = filterBy(filterData, filter.filter);
        setFilteredData(sconst);
    }

    const getDropDownData = () => {
        if (!context.data) {
            execute(props.verb, props.endpoint)
                .then(
                    ((result: any) => {
                        if (result) {
                            context.setData(result);
                            setFilteredData(result);
                        }
                    }),
                )
        }
    }

    const listNoDataRender = (element: React.ReactElement<HTMLDivElement>) => {
        const noData = (
            <h4 style={{ fontSize: "1em" }}>
                <span className="k-icon k-i-warning" style={{ fontSize: "2.5em" }} />
                <br />
                <br />
                No data please add an item.
            </h4>
        );
        return cloneElement(element, { ...element.props }, noData);
    };

    const handleChange = (event: ComboBoxChangeEvent) => {
        setSelected(event.target.value);
        if (event.target?.value?.id) {
            props.handleChange(event.target.value.id);
        } else {
            props.handleChange(0);
        }
    }

    const toggleAddClick = () => {
        setShowWindow(!showWindow);
    }

    const getStartingWindowWidth = () => {
        if (window.innerWidth > 700) {
            return 700;
        } else {
            return window.innerWidth;
        }
    }

    const getStartingWindowHeight = () => {
        if (window.innerHeight > 800) {
            return 800;
        } else {
            return window.innerHeight;
        }
    }

    useEffect(() => {
        selectFromFiltered();
    }, [filteredData]);

    useEffect(() => {
        setFilteredData(context.data);
    }, [context.data]);

    useEffect(() => {
        getDropDownData();
    }, [execute]);

    return (<span style={{ maxWidth: "300px", display: "block", width: "100%" }} >
        <span>
            {'Select a' + props.title}{props.required && "*"} &nbsp;&nbsp;
            {props.addObjectElement &&
                <span title={'Add a' + props.title} className="k-icon k-i-plus-outline" onClick={toggleAddClick}></span>}
            {selected?.id &&
                <>
                    <span style={{ float: "right", fontSize: "10pt" }} onClick={() => setShowAddress(!showAddress)} ref={anchor}>
                        {showAddress ?
                            <span style={{ color: "red" }}>Close</span> :
                            "View Address"}
                    </span>
                    <Popup anchor={anchor.current} show={showAddress} popupClass={"popup-content}"}>
                        <table style={{ margin: "8px" }}>
                            <tbody>
                                <tr>
                                    <td width="110px" style={{ fontWeight: "bold" }}>
                                        Address:
                                    </td>
                                    <td>
                                        {selected.addressLine1}
                                    </td>
                                </tr>
                                {selected.addressLine2 &&
                                    <tr>
                                        <td width="110px" style={{ fontWeight: "bold" }}>
                                        </td>
                                        <td>
                                            {selected.addressLine2}
                                        </td>
                                    </tr>}
                                <tr>
                                    <td width="110px" style={{ fontWeight: "bold" }}>
                                        City:
                                    </td>
                                    <td>
                                        {selected.city}
                                    </td>
                                </tr>
                                <tr>
                                    <td width="110px" style={{ fontWeight: "bold" }}>
                                        State:
                                    </td>
                                    <td>
                                        {selected.state}
                                    </td>
                                </tr>
                                <tr>
                                    <td width="110px" style={{ fontWeight: "bold" }}>
                                        Postal Code:
                                    </td>
                                    <td>
                                        {selected.postalCode}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Popup>
                </>}
        </span>
        <MultiColumnComboBox
            columns={[
                { field: "facilityName", header: "Facility Name", width: "200px" },
                { field: "addressLine1", header: "Address Line 1", width: "200px" },
                { field: "city", header: "City", width: "150px" },
                { field: "state", header: "State", width: "75px" },
                { field: "postalCode", header: "Postal Code", width: "100px" },
            ]}
            textField={"facilityName"}
            dataItemKey="id"
            data={filteredData}
            value={selected}
            filterable={true}
            onFilterChange={filterChange}
            listNoDataRender={listNoDataRender}
            onChange={handleChange}
            disabled={props.disabled}
        ></MultiColumnComboBox>
        {props.required && !(selected?.id) &&
            <Error>This is a required field</Error>}
        {showWindow && (
            <Window title={'Add a' + props.title} onClose={toggleAddClick} initialWidth={getStartingWindowWidth()} initialHeight={getStartingWindowHeight()}>
                {props.addObjectElement}
            </Window>)}
    </span>);
}